import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api";
import { mergeById, type SettlementTab } from "../payrollPresentation";
import { collectPendingSettlements } from "../settlementSelection";
import type { CursorPage, SettlementBatchSummary, SettlementBatchStatus, SettlementRecord, SettlementRecordPage, SettlementRecordStatus, SettlementSource, SettlementSort } from "../types";

type Collection = "records" | "batches";
type PayrollSnapshot = {
  key: string; batchKey: string;
  records: SettlementRecordPage; batches: CursorPage<SettlementBatchSummary>;
};
const empty = <T,>(): CursorPage<T> => ({ items: [], nextCursor: null });

export type { SettlementSort } from "../types";

/** 정산일 범위. 빈 값이면 기간 제한 없이 전체를 뜻한다. */
export type SettlementRange = { from: string; to: string } | null;

/** 표의 열 머리글이 거는 조건들. 값이 없는 열은 안 걸린 것이다. */
export type ColumnFilterValues = { workerId?: string; source?: SettlementSource; status?: SettlementRecordStatus; projectName?: string; subtaskName?: string };

export function usePayrollData(tab: SettlementTab, range: SettlementRange, batchId?: string, sort: SettlementSort = "date", column: ColumnFilterValues = {}, statusScope?: readonly SettlementRecordStatus[], unbatchedOnly = false, batchStatus?: SettlementBatchStatus, batchCursor?: string, batchLimit = 20, enabled = true, query = "", recordsOnly = false) {
  // 정렬은 조회 조건의 일부다. 키에 넣어 두면 정렬을 바꿀 때 목록을 처음부터 다시 받아온다.
  // 이전 정렬로 받아 둔 커서를 그대로 쓰면 페이지가 어긋난다.
  const key = JSON.stringify([tab, range?.from ?? "", range?.to ?? "", batchId ?? "", sort, column, statusScope ?? [], unbatchedOnly, batchStatus ?? "", batchCursor ?? "", batchLimit, query, recordsOnly]);
  // 상세 묶음의 선택은 기록 조회만 바꾼다. 묶음 목록은 같은 상태·페이지라면 유지한다.
  const batchKey = JSON.stringify([tab, batchStatus ?? "", batchCursor ?? "", batchLimit]);
  const scope = useRef(key); scope.current = key;
  const generation = useRef(0), more = useRef(new Set<string>());
  const [snapshot, setSnapshot] = useState<PayrollSnapshot | null>(null);
  const [status, setStatus] = useState({ key: "", loading: true, error: "" });
  const [paging, setPaging] = useState<Partial<Record<Collection, { loading: boolean; error: string }>>>({});
  /* 묶음을 지정해 본 때에만 기간을 비운다 — 그 묶음은 고른 범위 밖의 기록도 담을 수 있고,
     그때 보려는 것은 "그 달"이 아니라 "그 묶음 전부"다. */
  const period = batchId ? {} : { from: range?.from, to: range?.to };
    /* 열 머리글이 건 조건이 탭보다 구체적이면 그쪽을 쓴다 — 지금 탭은 늘 "전체"라 실질적으로
     열이 구분을 정한다. */
  /* 열 머리글이 상태를 직접 골랐으면 그쪽이 더 구체적이라 이긴다. 아니면 상태 칩이 정한
     범위를 쓴다 — "정산 예정"은 미정산과 정산 대기 둘을 함께 묻는 질문이다. */
  const filters = useCallback(() => ({ source: column.source ?? (tab === "all" ? undefined : tab), ...period, batchId, query: query || undefined, unbatchedOnly: unbatchedOnly ? "true" as const : undefined, sort, ...column,
    status: column.status ?? (statusScope ? [...statusScope] : undefined) }), [tab, range?.from, range?.to, batchId, sort, key]);
  const reload = useCallback(async () => {
    const current = ++generation.current;
    setStatus({ key, loading: true, error: "" }); setPaging({});
    try {
      // 미정산만 따로 받던 질의는 정산 내역 표가 상태 열로 대신한다.
      // This workspace renders only the list and its records. No consumer reads
      // global stats; do not delay it on an unused aggregate query.
      const [records, batches] = await Promise.all([
        api.listSettlements({ ...filters(), limit: 50, includeMetadata: "true" }),
        /* 묶음은 기간을 따르지 않는다. 한 묶음은 한 번의 이체이고, 이체·문자·정산 완료는 만든
           달과 상관없이 아직 안 끝났으면 해야 하는 일이다 — 9월을 보는 중에 8월에 만든
           묶음이 미완료로 남아 있다면 그것이야말로 보여야 한다. */
        // An expanded batch uses only records; its parent owns the fresh list.
        // No retained cache or cross-session response sharing.
        recordsOnly ? Promise.resolve(empty<SettlementBatchSummary>()) : api.listSettlementBatches({ source: tab === "all" ? undefined : tab, status: batchStatus, cursor: batchCursor, limit: batchLimit }),
      ]);
      if (scope.current !== key || generation.current !== current) return;
      // 표와 그 위의 묶음 목록은 같은 조회 결과로 한 번에 교체한다.
      setSnapshot({ key, batchKey, records, batches });
      setStatus({ key, loading: false, error: "" });
    } catch (cause) {
      if (scope.current === key && generation.current === current) setStatus({ key, loading: false, error: cause instanceof Error ? cause.message : "정산 정보를 불러오지 못했습니다." });
      throw cause;
    }
  }, [key, batchKey, filters, tab, range?.from, range?.to, batchStatus, batchCursor, batchLimit, recordsOnly]);
  useEffect(() => { if (enabled) void reload().catch(() => undefined); return () => { generation.current++; }; }, [reload, enabled]);
  const page = {
    records: snapshot?.key === key ? snapshot.records : { ...empty<SettlementRecord>(), filterOptions: undefined, total: undefined } as SettlementRecordPage,
    batches: snapshot?.batchKey === batchKey ? snapshot.batches : empty<SettlementBatchSummary>(),
  };
  const loading = enabled && (status.key !== key || status.loading);
  const error = status.key === key ? status.error : "";
  const ready = enabled && !loading && !error && snapshot?.key === key && snapshot.batchKey === batchKey;
  const loadMore = async (collection: Collection) => {
    const cursor = page[collection].nextCursor, current = generation.current, token = `${current}/${collection}`;
    if (!cursor || loading || error || more.current.has(token)) return;
    more.current.add(token); setPaging(value => ({ ...value, [collection]: { loading: true, error: "" } }));
    try {
      const next = collection === "batches" ? await api.listSettlementBatches({ source: tab === "all" ? undefined : tab, status: batchStatus, cursor, limit: batchLimit }) : await api.listSettlements({ ...filters(), cursor, limit: 50 });
      if (scope.current !== key || generation.current !== current) return;
      setSnapshot(value => {
        if (!value || value.key !== key || value.batchKey !== batchKey) return value;
        return collection === "batches"
          ? { ...value, batches: { items: mergeById(value.batches.items, next.items as SettlementBatchSummary[]), nextCursor: next.nextCursor } }
          : { ...value, records: { ...value.records, items: mergeById(value.records.items, next.items as SettlementRecord[]), nextCursor: next.nextCursor } };
      });
      setPaging(value => ({ ...value, [collection]: { loading: false, error: "" } }));
    } catch (cause) {
      if (scope.current === key && generation.current === current) setPaging(value => ({ ...value, [collection]: { loading: false, error: cause instanceof Error ? cause.message : "추가 내역을 불러오지 못했습니다." } }));
    } finally { more.current.delete(token); }
  };
  const loadAllPending = (signal: AbortSignal) => collectPendingSettlements(cursor =>
    api.listSettlements({ ...filters(), status: "pending", unbatchedOnly: "true", cursor, limit: 100 }, signal), signal);
  return { ...page, key, ready, loading, error, paging, reload, loadMore, loadAllPending };
}
